import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import MeaCreateModal from "../../../components/MeaCreateModal";
import MeaStudentAvatar from "../../trainings/components/MeaStudentAvatar";
import SlotPreferencePicker from "../../trainings/slot-change-requests/components/SlotPreferencePicker";
import familyAPI from "../../../services/family";
import {
  buildPreferredSlotOptions,
  formatSlotLabel,
  slotsForClass,
  studentClassId,
  studentDisplayName,
  studentLevelId,
} from "../../trainings/slot-change-requests/slotChangeUtils";
import { PERMISSION_TRAINING_STUDENTS } from "../../../permissions";

const familySlotChangeRequestAPI = {
  CreateOne: (_schoolId, _trainingId, accessToken, data) =>
    familyAPI.CreateSlotChangeRequest(accessToken, data),
};

const FamilyCreateSlotChangeRequestModal = forwardRef((props, ref) => {
  const innerRef = React.useRef();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [preferredSlotIds, setPreferredSlotIds] = useState([]);
  const [reason, setReason] = useState("");

  const students = props.students || [];
  const slotChangeData = props.slotChangeData || {};
  const slots = slotChangeData.slots || [];
  const classes = slotChangeData.classes || [];
  const scheduleItems = slotChangeData.schedule_items || [];

  const currentLevelId = studentLevelId(selectedStudent);
  const currentClassId = studentClassId(selectedStudent);
  const currentClassName =
    selectedStudent?.classe?.name || selectedStudent?.class?.name || "";

  const studentOptions = useMemo(
    () =>
      students
        .filter((student) => studentClassId(student))
        .map((student) => ({
          ...student,
          label:
            studentDisplayName(student) +
            (student?.classe?.name ? ` — ${student.classe.name}` : ""),
        })),
    [students]
  );

  const slotOptions = useMemo(() => {
    if (!selectedStudent || !currentLevelId) {
      return [];
    }
    return buildPreferredSlotOptions({
      classes,
      slots,
      scheduleItems,
      currentClassId,
      levelId: currentLevelId,
    });
  }, [selectedStudent, currentLevelId, classes, slots, scheduleItems, currentClassId]);

  const currentSlots = useMemo(
    () => slotsForClass({ slots, scheduleItems, classId: currentClassId }),
    [slots, scheduleItems, currentClassId]
  );

  const resetForm = (student) => {
    setSelectedStudent(student || null);
    setPreferredSlotIds([]);
    setReason("");
  };

  useImperativeHandle(ref, () => ({
    show: (callback, student) => {
      resetForm(student || null);
      innerRef.current.show(callback);
    },
  }));

  return (
    <MeaCreateModal
      ref={innerRef}
      title="Nouvelle demande de changement de créneau"
      schoolId={props.schoolId}
      trainingId={props.trainingId}
      accessToken={props.accessToken}
      serviceAPI={familySlotChangeRequestAPI}
      submitDisabled={!selectedStudent}
      onSubmitSuccess={props.onSubmitSuccess}
      data={{
        student_id: selectedStudent?.id,
        preferred_slot_ids: preferredSlotIds,
        requested_at: new Date().toISOString(),
        urgency: "NORMAL",
        reason: reason || null,
      }}
    >
       <div className="mb-3 p-3 border-round bg-yellow-50 border-1 border-yellow-200 text-700 text-sm line-height-3">
        Votre demande sera transmise à l'administration. Elle sera ensuite
        validée ou refusée en fonction des places disponibles sur les créneaux
        demandés. Vous serez informé par mail en conséquence.
      </div>
      {selectedStudent ? (
        <div className="mb-3">
          <label className="block text-900 font-medium mb-2">Élève</label>
          <MeaStudentAvatar
            student={selectedStudent}
            schoolId={props.schoolId}
            trainingId={props.trainingId}
            permission={PERMISSION_TRAINING_STUDENTS.GET}
          />
          <small className="block text-600 mt-2">
            {currentClassName && (
              <span className="block">
                Classe actuelle :{" "}
                <span className="font-medium text-900">{currentClassName}</span>
              </span>
            )}
            <span className="block">
              Créneau actuel :{" "}
              <span className="font-medium text-900">
                {currentSlots.length
                  ? currentSlots.map((slot) => formatSlotLabel(slot)).join(", ")
                  : "Aucun créneau"}
              </span>
            </span>
          </small>
          <Dropdown
            value={studentOptions.find((student) => student.id === selectedStudent?.id)}
            onChange={(e) => {
              setSelectedStudent(e.value);
              setPreferredSlotIds([]);
            }}
            options={studentOptions}
            optionLabel="label"
            filter
            placeholder="Changer d'élève"
            className="w-full mt-2"
          />
        </div>
      ) : (
        <>
          <label className="block text-900 font-medium mb-2">Élève</label>
          <Dropdown
            value={studentOptions.find((student) => student.id === selectedStudent?.id)}
            onChange={(e) => {
              setSelectedStudent(e.value);
              setPreferredSlotIds([]);
            }}
            options={studentOptions}
            optionLabel="label"
            filter
            placeholder="Sélectionner un élève"
            className="w-full mb-3"
          />
        </>
      )}

      <label className="block text-900 font-medium mb-2">
        Créneaux souhaités (plusieurs choix possibles par ordre de préférence)
      </label>
      
      <SlotPreferencePicker
        options={slotOptions}
        value={preferredSlotIds}
        onChange={setPreferredSlotIds}
        disabled={!selectedStudent}
        placeholder={
          !selectedStudent
            ? "Sélectionner un élève d'abord"
            : slotOptions.length
              ? "Ajouter un créneau"
              : "Aucun autre créneau du même niveau"
        }
      />
      {selectedStudent && !slotOptions.length ? (
        <small className="block text-600 mb-3">
          Aucun autre créneau du même niveau n'est disponible. La demande peut
          quand même être créée pour permettre un refus motivé et l'envoi d'un
          mail aux parents.
        </small>
      ) : null}

      <label className="block text-900 font-medium mb-2">Motif</label>
      <InputTextarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={5}
        className="w-full mb-3"
        placeholder="Contrainte familiale, transport..."
      />
    </MeaCreateModal>
  );
});

export default FamilyCreateSlotChangeRequestModal;
